import {
  batchSet,
  setSandboxMode,
  seedSandbox,
  seedSandboxSingleton,
  clearSandbox,
} from "./firestoreHelpers";
import { saveConfigAlquileres } from "./configuracion";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";

// localStorage keys -> Firestore collection names. Match the keys used by the
// pre-migration application/*.js files (which are gone now -- this is the only
// place that still reads them).
const ENTITIES = [
  { name: "contratos",     storage: "alquileres_contratos",     coll: "alquileres_contratos" },
  { name: "locadores",     storage: "alquileres_locadores",     coll: "alquileres_locadores" },
  { name: "locatarios",    storage: "alquileres_locatarios",    coll: "alquileres_locatarios" },
  { name: "pagos",         storage: "alquileres_pagos",         coll: "alquileres_pagos" },
  { name: "gastos",        storage: "alquileres_gastos",        coll: "alquileres_gastos" },
  { name: "liquidaciones", storage: "alquileres_liquidaciones", coll: "alquileres_liquidaciones" },
  { name: "indices",       storage: "alquileres_indices",       coll: "alquileres_indices" },
];

const CONFIG_STORAGE = "alquileres_config";

const normalizeName = (name) =>
  String(name || "").trim().toLowerCase().replace(/\s+/g, " ");

const readArray = (key) => {
  const raw = localStorage.getItem(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Returns { contratos: { storage: 5, uploaded: 5 }, ... }
export const previewLocalStorage = () => {
  const out = {};
  for (const { name, storage } of ENTITIES) {
    out[name] = { storage: readArray(storage).length };
  }
  const cfg = localStorage.getItem(CONFIG_STORAGE);
  out.config = { storage: cfg ? 1 : 0 };
  return out;
};

export const migrateLocalStorageToFirestore = async (onProgress = () => {}) => {
  const summary = {};

  for (const { name, storage, coll } of ENTITIES) {
    let items = readArray(storage);
    if (!items.length) {
      summary[name] = 0;
      onProgress({ name, status: "skipped", count: 0 });
      continue;
    }

    // Backfill nombreNormalizado for dedup queries on future saves.
    if (name === "locadores" || name === "locatarios") {
      items = items.map((it) => ({
        ...it,
        nombreNormalizado: it.nombreNormalizado || normalizeName(it.nombre),
      }));
    }

    onProgress({ name, status: "uploading", count: items.length });
    const written = await batchSet(coll, items);
    summary[name] = written;
    onProgress({ name, status: "done", count: written });
  }

  const configRaw = localStorage.getItem(CONFIG_STORAGE);
  if (configRaw) {
    try {
      const config = JSON.parse(configRaw);
      await saveConfigAlquileres(config);
      summary.config = 1;
      onProgress({ name: "config", status: "done", count: 1 });
    } catch {
      summary.config = 0;
    }
  } else {
    summary.config = 0;
  }

  return summary;
};

export const clearLocalStorageAlquileres = () => {
  for (const { storage } of ENTITIES) localStorage.removeItem(storage);
  localStorage.removeItem(CONFIG_STORAGE);
};

// Sandbox helpers ---------------------------------------------------------

// Snapshot every Firestore collection used by the rentals module into the
// sandbox storage and turn sandbox mode on. After calling this the user can
// refresh and play freely without affecting the live data in Firestore.
// Reads bypass isSandboxMode() by going through firebase/firestore directly.
export const enterSandbox = async (onProgress = () => {}) => {
  setSandboxMode(false); // make absolutely sure we read from Firestore below
  const summary = {};

  for (const { name, coll } of ENTITIES) {
    onProgress({ name, status: "reading" });
    const snap = await getDocs(collection(db, coll));
    const items = snap.docs.map((d) => ({ idFirebase: d.id, ...d.data() }));
    seedSandbox(coll, items);
    summary[name] = items.length;
    onProgress({ name, status: "done", count: items.length });
  }

  // Config singleton
  const cfgSnap = await getDoc(doc(db, "alquileres_config", "main"));
  if (cfgSnap.exists()) {
    seedSandboxSingleton("alquileres_config", "main", cfgSnap.data());
    summary.config = 1;
  } else {
    summary.config = 0;
  }

  setSandboxMode(true);
  return summary;
};

export const exitSandbox = ({ clearData = false } = {}) => {
  setSandboxMode(false);
  if (clearData) clearSandbox();
};

// Wipe the sandbox without changing the active mode.
export const resetSandbox = () => clearSandbox();
